import { StatusCode } from "api/protocols";

import Card from "components/card/Card";
import Page from "components/page/Page";
import { deriveTableShortTitle, isEmpty, isPositiveInteger } from "helpers/Helpers";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "redux/actions/ui";
import FloorPlanTable from "../floorPlanTable/FloorPlanTable";
import FloorPlan, { IFloorPlanBoundingRectangle } from "../floorPlan/FloorPlan";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { useHistory, useParams } from "react-router";
import "./floorPlanEdit.scss";

import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import {
  DeleteFloorPlan,
  GetFloorPlans,
  GetFloorPlanTemplate,
  GetTable,
  GetTableElements,
  IFloorPlan,
  IFloorPlanTable,
  IFloorPlanTemplate,
  ITable,
  ITableElement,
  PostFloorPlanTemplate,
  PostTable,
  PutArchiveTable,
  PutFloorPlan,
  PutFloorPlanTemplate,
  PutTable,
} from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { GetRegisterGroups } from "api/rpc/2024-04/facilityAdmin/facility/register/group";
import { GetKitchenLocations } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/location";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import Toggle from "components/form/toggle/Toggle";
import { useAppDispatch } from "hooks/redux";
import SelectNew from "components/select/SelectNew";
import Popup from "components/popup/Popup";

interface IFloorPlanEditState {
  floorPlanId: number;

  floorPlan: IFloorPlan;
  floorPlanBeforeChanges: IFloorPlan;
  floorPlanLoaded: boolean;

  floorPlanTableElements: ITableElement[];
  floorPlanTableElementsLoaded: boolean;

  floorPlanTables: IFloorPlanTable[];
  floorPlanTablesBeforeChanges: IFloorPlanTable[];
  floorPlanTablesLoaded: boolean;

  floorPlanGridWidthInput: string;
  floorPlanGridHeightInput: string;

  floorPlanTablesBoundingRectangles: IFloorPlanBoundingRectangle[];

  register_group_id: number;
  register_groups: any[];

  kitchenLocations: any[];

  displaySeatNumbers: boolean;

  deleteFloorPlanWarning: boolean;
}

interface IParams {
  floorPlanId: string;
}

interface ITemplateState {
  templates: IFloorPlanTemplate[];
  floorPlanTemplateTables?: IFloorPlanTable[][];
}

export const ItemTypes = {
  TABLE: "table",
};

export const DEFAULT_TABLE_DIMENSION = 40;
const DEFAULT_NO_TEMPLATE_INDEX = -99;

export default function FloorPlanEdit() {
  const history = useHistory();
  const { floorPlanId } = useParams<IParams>();
  const { t } = useTranslation();
  const MAX_GRID_DIMENSION = 75;
  const MIN_GRID_DIMENSION = 10;
  const { Option } = Select;
  const dispatch = useAppDispatch();


  const [floorPlanTemplates, setFloorPlanTemplates] = useState<ITemplateState>({
    templates: [],
    floorPlanTemplateTables: [],
  });

  const [selectedTemplate, setSelectedTemplate] = useState<number>(DEFAULT_NO_TEMPLATE_INDEX);
  const [floorPlanEditState, setFloorPlanEditState] = useState<IFloorPlanEditState>({
    floorPlanId: isPositiveInteger(floorPlanId) ? parseInt(floorPlanId) : undefined,

    floorPlan: undefined,
    floorPlanBeforeChanges: undefined,
    floorPlanLoaded: false,

    floorPlanTableElements: [],
    floorPlanTableElementsLoaded: false,

    floorPlanTables: [],
    floorPlanTablesBeforeChanges: undefined,
    floorPlanTablesLoaded: false,

    floorPlanGridWidthInput: "",
    floorPlanGridHeightInput: "",

    floorPlanTablesBoundingRectangles: [],

    register_group_id: -1,
    register_groups: [],

    kitchenLocations: [],

    displaySeatNumbers: false,

    deleteFloorPlanWarning: false,
  });

  useEffect(() => {
    loadFloorPlanEdit();
    void loadRegisterGroups();
    void loadKitchenLocations();
  }, []);

  function loadFloorPlanEdit() {
    if (!floorPlanEditState.floorPlanId) {
      dispatch(showError(t("Invalid floor plan id")));
    } else {
      void loadFloorPlanTemplates();
      void loadFloorPlanTableElements();
      void loadFloorPlanTables();
      void loadFloorPlan();
    }
  }

  async function loadFloorPlanTemplates() {
    const getFloorPlanTemplates = await GetFloorPlanTemplate({}, true);

    if (getFloorPlanTemplates.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading floor plan templates")));
      return;
    }

    setFloorPlanTemplates(prev => ({
      ...prev,
      templates: getFloorPlanTemplates.data,
    }));
  }

  async function loadFloorPlanTableElements() {
    const getFloorPlanTableElementsResponse = await GetTableElements(true);

    if (
      getFloorPlanTableElementsResponse.status !== StatusCode.OK
      // getFloorPlanTableElementsResponse.data === undefined
    ) {
      dispatch(showError(t("Failed to load floor plan table elements")));
      return;
    }

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanTableElements: getFloorPlanTableElementsResponse.data,
      floorPlanTableElementsLoaded: true,
    }));
  }

  async function loadKitchenLocations() {
    const kitchenLocationRes = await GetKitchenLocations(null, false);
    if (kitchenLocationRes.status !== StatusCode.OK) {
      return;
    }

    const kitchenLocations = kitchenLocationRes.data as Array<Partial<TKitchenLocation>>;

    kitchenLocations.unshift({ title: t("None"), id: -1 });
    setFloorPlanEditState(prev => ({ ...prev, kitchenLocations: kitchenLocations }));
  }

  function toFloorPlanTables(tables: ITable[], replace?: boolean): IFloorPlanTable[] {
    if (tables.length === 0 || tables === undefined || tables === null) {
      return [];
    } else {
      const floorPlanTables: IFloorPlanTable[] = tables.map(table => {
        const floorPlanTable: IFloorPlanTable = {
          id: String(table.id),
          x_coordinate: table.x_coordinate,
          y_coordinate: table.y_coordinate,
          seats: table.seats,
          title: table.title,
          short_title: deriveTableShortTitle(table.title) || table.short_title,
          table_element_id: table.table_element_id,
          floor_plan_id: replace ? floorPlanEditState.floorPlanId : table.floor_plan_id,
          template_id: replace ? null : table.template_id,
          rotation: table.rotation,
          open: !!table.open,
        };

        return floorPlanTable;
      });

      return floorPlanTables;
    }
  }

  async function loadFloorPlanTables() {
    const getFloorPlanTablesResponse = await GetTable({ floor_plan_id: floorPlanEditState.floorPlanId }, true);

    if (getFloorPlanTablesResponse.status !== StatusCode.OK || getFloorPlanTablesResponse.data === undefined) {
      dispatch(showError(t("Failed to load floor plan tables")));
    } else {
      const loadedFloorPlanTables = toFloorPlanTables(getFloorPlanTablesResponse.data);

      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlanTables: loadedFloorPlanTables,
        floorPlanTablesLoaded: true,
      }));
    }
  }

  async function loadFloorPlan() {
    const getFloorPlanResponse = await GetFloorPlans({ id: floorPlanEditState.floorPlanId }, true);

    if (getFloorPlanResponse.status !== StatusCode.OK || getFloorPlanResponse.data?.[0] === undefined) {
      dispatch(showError(t("Failed to load floor plan")));
    } else {
      const floorPlan = getFloorPlanResponse.data[0];

      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...floorPlan,
          kitchen_location_id_1: floorPlan.kitchen_location_id_1 ?? -1,
          kitchen_location_id_2: floorPlan.kitchen_location_id_2 ?? -1,
          kitchen_location_id_3: floorPlan.kitchen_location_id_3 ?? -1,
          register_group_id: floorPlan.register_group_id ?? -1,
          display_seat_numbers: floorPlan?.display_seat_numbers,
        },
        floorPlanLoaded: true,
        floorPlanGridWidthInput: String(floorPlan.grid_width),
        floorPlanGridHeightInput: String(floorPlan.grid_height),
        register_group_id: floorPlan.register_group_id ?? -1,
        displaySeatNumbers: !!floorPlan?.display_seat_numbers,
      }));
    }
  }

  function isGridDimensionInBounds(value: string) {
    if (isPositiveInteger(value)) {
      const dimension = parseInt(value);

      return dimension >= MIN_GRID_DIMENSION && dimension <= MAX_GRID_DIMENSION;
    } else {
      return false;
    }
  }

  function isValidGridWidthDimension(value: string) {
    if (isGridDimensionInBounds(value)) {
      return isValidGridDimension(parseInt(value), floorPlanEditState.floorPlan.grid_height);
    } else {
      return false;
    }
  }

  function isValidGridHeightDimension(value: string) {
    if (isGridDimensionInBounds(value)) {
      return isValidGridDimension(floorPlanEditState.floorPlan.grid_width, parseInt(value));
    } else {
      return false;
    }
  }

  function isValidGridDimension(width: number, height: number) {
    return !floorPlanEditState.floorPlanTablesBoundingRectangles.some(boundingRectangle => {
      const bottomRightGridCoordinate = boundingRectangle.bottomRight;

      return bottomRightGridCoordinate.x > width || bottomRightGridCoordinate.y > height;
    });
  }

  useEffect(() => {
    if (isValidGridWidthDimension(floorPlanEditState.floorPlanGridWidthInput)) {
      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...prev.floorPlan,
          grid_width: parseInt(floorPlanEditState.floorPlanGridWidthInput),
        },
      }));
    }
  }, [floorPlanEditState.floorPlanGridWidthInput]);

  useEffect(() => {
    if (isValidGridHeightDimension(floorPlanEditState.floorPlanGridHeightInput)) {
      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...prev.floorPlan,
          grid_height: parseInt(floorPlanEditState.floorPlanGridHeightInput),
        },
      }));
    }
  }, [floorPlanEditState.floorPlanGridHeightInput]);

  useEffect(() => {
    if (
      !floorPlanEditState.floorPlanLoaded ||
      !floorPlanEditState.floorPlanTablesLoaded ||
      floorPlanEditState.floorPlanBeforeChanges !== undefined ||
      floorPlanEditState.floorPlanTablesBeforeChanges !== undefined
    ) {
      return;
    }

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanBeforeChanges: prev.floorPlan,
      floorPlanTablesBeforeChanges: prev.floorPlanTables,
    }));
  }, [
    floorPlanEditState.floorPlanLoaded,
    floorPlanEditState.floorPlanTablesLoaded,
    floorPlanEditState.floorPlanBeforeChanges,
    floorPlanEditState.floorPlanTablesBeforeChanges,
  ]);

  function unsavedChangesExist() {
    if (
      floorPlanEditState.floorPlanBeforeChanges === undefined ||
      floorPlanEditState.floorPlanTablesBeforeChanges === undefined
    ) {
      return false;
    }

    return (
      !isEqual(floorPlanEditState.floorPlanBeforeChanges, floorPlanEditState.floorPlan) ||
      !isEqual(floorPlanEditState.floorPlanTablesBeforeChanges, floorPlanEditState.floorPlanTables)
    );
  }

  function isUUID(value: string) {
    return value.length === 36 && !isPositiveInteger(value);
  }

  async function saveFloorPlanTables() {
    if (isEmpty(floorPlanEditState.floorPlan.title)) {
      dispatch(showError(t("Invalid floor plan title")));
      return;
    }

    if (floorPlanEditState.floorPlan.register_group_id === -1) {
      dispatch(showError(t("A Register Group is required to save your floor plan.")));
      return;
    }

    const putFloorPlanResponse = await PutFloorPlan(
      {
        id: floorPlanEditState.floorPlan.id,
        title: floorPlanEditState.floorPlan.title,
        grid_width: floorPlanEditState.floorPlan.grid_width,
        grid_height: floorPlanEditState.floorPlan.grid_height,
        register_group_id: floorPlanEditState.floorPlan.register_group_id,
        kitchen_location_id_1: negativeCheckHelper(floorPlanEditState.floorPlan.kitchen_location_id_1),
        kitchen_location_id_2: negativeCheckHelper(floorPlanEditState.floorPlan.kitchen_location_id_2),
        kitchen_location_id_3: negativeCheckHelper(floorPlanEditState.floorPlan.kitchen_location_id_3),
        display_seat_numbers: !!floorPlanEditState?.floorPlan?.display_seat_numbers,
      },
      true,
    );

    if (putFloorPlanResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to update floor plan")));
      return;
    }

    const tablesToUpdate = floorPlanEditState.floorPlanTables.filter(table => !isUUID(table.id));
    const tablesToCreate = floorPlanEditState.floorPlanTables.filter(table => isUUID(table.id));
    const tablesToArchive = floorPlanEditState.floorPlanTablesBeforeChanges.filter(
      initialTable => !floorPlanEditState.floorPlanTables.some(table => table.id === initialTable.id),
    );

    let updatedTables: IFloorPlanTable[] = [];
    let createdTables: IFloorPlanTable[] = [];

    if (tablesToUpdate.length > 0) {
      const putFloorPlanTablesResponse = await PutTable({ tables: tablesToUpdate }, true);

      if (putFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to update floor plan tables")));
        return;
      }

      updatedTables = toFloorPlanTables(putFloorPlanTablesResponse.data);
    }

    if (tablesToCreate.length > 0) {
      const postFloorPlanTablesResponse = await PostTable(
        {
          tables: tablesToCreate.map(table => {
            const { ...remainder } = table;
            const postTable: Omit<IFloorPlanTable, "id"> = remainder;

            return postTable;
          }),
        },
        true,
      );

      if (postFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to create new tables")));
        return;
      }

      createdTables = toFloorPlanTables(postFloorPlanTablesResponse.data);
    }

    if (tablesToArchive.length > 0) {
      const archiveFloorPlanTablesResponse = await PutArchiveTable(
        {
          table_ids: tablesToArchive.map(table => parseInt(table.id)),
        },
        true,
      );

      if (archiveFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to delete tables")));
        return;
      }
    }

    const updatedFloorPlanTables: IFloorPlanTable[] = [...updatedTables, ...createdTables];

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanBeforeChanges: prev.floorPlan,
      floorPlanTables: updatedFloorPlanTables,
      floorPlanTablesBeforeChanges: updatedFloorPlanTables,
      floorPlanGridWidthInput: String(prev.floorPlan.grid_width),
      floorPlanGridHeightInput: String(prev.floorPlan.grid_height),
    }));
  }

  async function deleteFloorPlan() {
    if (floorPlanEditState.floorPlanTables.some(table => table.open)) {
      dispatch(showError(t("Cannot delete floor plan with open tables")));
      setFloorPlanEditState(prev => ({ ...prev, deleteFloorPlanWarning: false }));
      return;
    }

    const deleteFloorPlanResponse = await DeleteFloorPlan(
      {
        id: floorPlanEditState.floorPlan.id,
      },
      true,
    );

    if (deleteFloorPlanResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to delete floor plan")));
      return;
    }

    history.push("/admin/settings/floor-plan");
  }

  function handleOpenDeleteFloorPlanWarning() {
    setFloorPlanEditState(prev => ({ ...prev, deleteFloorPlanWarning: true }));
  }

  function cancelUnsavedChanges() {
    setSelectedTemplate(DEFAULT_NO_TEMPLATE_INDEX);
    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlan: prev.floorPlanBeforeChanges,
      floorPlanTables: prev.floorPlanTablesBeforeChanges,
      floorPlanGridWidthInput: String(prev.floorPlanBeforeChanges.grid_width),
      floorPlanGridHeightInput: String(prev.floorPlanBeforeChanges.grid_height),
    }));
  }

  function setFloorPlanTables(updateTables: (prevTables: IFloorPlanTable[]) => IFloorPlanTable[]): void {
    setFloorPlanEditState(prev => ({ ...prev, floorPlanTables: updateTables(prev.floorPlanTables) }));
  }

  function setFloorPlanTablesBoundingRectangles(boundingRectangles: IFloorPlanBoundingRectangle[]) {
    setFloorPlanEditState(prev => ({ ...prev, floorPlanTablesBoundingRectangles: boundingRectangles }));
  }

  function setFloorPlanTitle(e: any) {
    setFloorPlanEditState(prev => ({ ...prev, floorPlan: { ...prev.floorPlan, title: e.target.value } }));
  }

  function handleDropDownChange(value: any, property: keyof IFloorPlan) {
    setFloorPlanEditState(prev => ({ ...prev, floorPlan: { ...prev.floorPlan, [property]: value } }));
  }

  async function handleApplyTemplateSelect(value: number) {
    setSelectedTemplate(value);
    if (value === DEFAULT_NO_TEMPLATE_INDEX) {
      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlanTables: prev.floorPlanTablesBeforeChanges,
        floorPlanGridHeightInput: String(floorPlanEditState.floorPlan.grid_height),
        floorPlanGridWidthInput: String(floorPlanEditState.floorPlan.grid_width),
      }));
    } else {
      if (value in floorPlanTemplates.floorPlanTemplateTables) {
        const selectedFloorPlan = floorPlanTemplates.templates.filter(template => template.id === value);
        setFloorPlanEditState(prev => ({
          ...prev,
          floorPlanGridHeightInput: String(selectedFloorPlan[0].grid_height),
          floorPlanGridWidthInput: String(selectedFloorPlan[0].grid_width),
          floorPlanTables: floorPlanTemplates.floorPlanTemplateTables[value],
        }));
      } else {
        const getFloorPlanTemplateTablesRes = await GetTable({ template_id: value }, true);

        const floorPlanBufferObject = { ...floorPlanTemplates.floorPlanTemplateTables };

        if (
          getFloorPlanTemplateTablesRes.status !== StatusCode.OK ||
          getFloorPlanTemplateTablesRes.data === undefined
        ) {
          dispatch(showError(t("Failed to load floor plan template tables")));
          return;
        }

        const loadedFloorPlanTables = toFloorPlanTables(getFloorPlanTemplateTablesRes.data);
        loadedFloorPlanTables.forEach(table => {
          const newTableId = self.crypto.randomUUID();
          table.id = newTableId;
          table.floor_plan_id = Number(floorPlanId);
          table.template_id = undefined;
        });

        const selectedFloorPlan = floorPlanTemplates.templates.filter(template => template.id === value);
        setFloorPlanEditState(prev => ({
          ...prev,
          floorPlanGridHeightInput: String(selectedFloorPlan[0].grid_height),
          floorPlanGridWidthInput: String(selectedFloorPlan[0].grid_width),
          floorPlanTables: loadedFloorPlanTables,
        }));

        setFloorPlanTemplates(prev => ({
          ...prev,
          floorPlanTemplateTables: { ...floorPlanBufferObject, [value]: loadedFloorPlanTables },
        }));
      }
    }
  }

  async function loadRegisterGroups() {
    const res = await GetRegisterGroups(undefined, false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    res.data.unshift({ title: t("None"), id: -1 });

    setFloorPlanEditState(prevState => ({
      ...prevState,
      register_groups: res.data,
    }));
  }

  async function handleGenerateTemplate() {
    const newTemplateParams = {
      title: floorPlanEditState.floorPlan.title,
    };

    const newTemplateRes = await PostFloorPlanTemplate(newTemplateParams, false);

    if (newTemplateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating floor plan template")));
      return;
    }

    const newTemplateID = newTemplateRes.data.id;
    const floorPlanTemplateParams = {
      id: newTemplateRes.data.id,
      grid_width: floorPlanEditState.floorPlan.grid_width,
      grid_height: floorPlanEditState.floorPlan.grid_height,
    };

    const updateTemplateRes = await PutFloorPlanTemplate(floorPlanTemplateParams, false);
    if (updateTemplateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating floor plan template")));
      return;
    }

    const tablesToUpdate = floorPlanEditState.floorPlanTables.filter(table => !isUUID(table.id));
    const tablesToCreate = floorPlanEditState.floorPlanTables.filter(table => isUUID(table.id));

    let updatedTables: IFloorPlanTable[] = [];
    let createdTables: IFloorPlanTable[] = [];

    if (tablesToUpdate.length > 0) {
      const putFloorPlanTablesResponse = await PostTable(
        {
          tables: tablesToUpdate.map(table => {
            table.floor_plan_id = null;
            table.template_id = newTemplateID;
            const { ...remainder } = table;
            const postTable: Omit<IFloorPlanTable, "id"> = remainder;

            return postTable;
          }),
        },
        true,
      );

      if (putFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to update floor plan tables")));
        return;
      }

      updatedTables = toFloorPlanTables(putFloorPlanTablesResponse.data);
    }

    if (tablesToCreate.length > 0) {
      const postFloorPlanTablesResponse = await PostTable(
        {
          tables: tablesToCreate.map(table => {
            table.floor_plan_id = null;
            table.template_id = newTemplateID;
            const { ...remainder } = table;
            const postTable: Omit<IFloorPlanTable, "id"> = remainder;

            return postTable;
          }),
        },
        true,
      );

      if (postFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to create new tables")));
        return;
      }

      createdTables = toFloorPlanTables(postFloorPlanTablesResponse.data);
    }

    const updatedFloorPlanTables: IFloorPlanTable[] = [...updatedTables, ...createdTables];

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanBeforeChanges: prev.floorPlan,
      floorPlanTables: updatedFloorPlanTables,
      floorPlanTablesBeforeChanges: updatedFloorPlanTables,
      floorPlanGridWidthInput: String(prev.floorPlan.grid_width),
      floorPlanGridHeightInput: String(prev.floorPlan.grid_height),
    }));

    dispatch(showSuccess(t("Floor plan saved as template successfully")));
  }

  return (
    <Page
      title={t("Floor Plan")}
      subtitle={t("Manage floor plan")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Floor Plans"),
          url: "/admin/settings/floor-plan",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveFloorPlanTables,
        onCancel: cancelUnsavedChanges,
      }}
      multipleActionDropdownAction={{
        label: "Options",
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Save as Template"),
              handler: handleGenerateTemplate,
              icon: "floppy-disk",
            },
            {
              type: "handler",
              label: t("Delete Floor Plan"),
              handler: handleOpenDeleteFloorPlanWarning,
              icon: "trash",
            },
          ],
        },
      }}
    >
      {floorPlanEditState.floorPlanTableElementsLoaded &&
        floorPlanEditState.floorPlanTablesLoaded &&
        floorPlanEditState.floorPlanLoaded && (
          <div>
            <Card>
              <Card.Section title={t("Floor Plan Settings")}>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      label={t("Title")}
                      value={floorPlanEditState.floorPlan.title}
                      onChange={setFloorPlanTitle}
                      error={isEmpty(floorPlanEditState.floorPlan.title)}
                    />
                    <Select
                      label={t("Register Group")}
                      onChange={(value: any) => handleDropDownChange(value, "register_group_id")}
                      defaultValue={floorPlanEditState.floorPlan.register_group_id}
                    >
                      {floorPlanEditState.register_groups.map((registerGroup: any, index: number) => {
                        return (
                          <Option
                            className={index === 0 ? "first-register-group_dropdown" : ""}
                            key={index}
                            value={registerGroup.id}
                            name={registerGroup.title}
                          >
                            {registerGroup.title}
                          </Option>
                        );
                      })}
                    </Select>
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      label={t("Grid Width")}
                      value={floorPlanEditState.floorPlanGridWidthInput}
                      onChange={(e: any) =>
                        setFloorPlanEditState(prev => ({ ...prev, floorPlanGridWidthInput: e.target.value }))
                      }
                      error={!isValidGridWidthDimension(floorPlanEditState.floorPlanGridWidthInput)}
                    />
                    <Input
                      label={t("Grid Height")}
                      value={floorPlanEditState.floorPlanGridHeightInput}
                      onChange={(e: any) =>
                        setFloorPlanEditState(prev => ({ ...prev, floorPlanGridHeightInput: e.target.value }))
                      }
                      error={!isValidGridHeightDimension(floorPlanEditState.floorPlanGridHeightInput)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <SelectNew
                      label={t("Templates")}
                      id={"selected_template"}
                      options={[
                        {
                          id: DEFAULT_NO_TEMPLATE_INDEX,
                          label: t("No Template"),
                        },
                        ...floorPlanTemplates.templates.map(template => ({
                          id: template.id,
                          label: template.title,
                          disabled: template.id === selectedTemplate,
                        })),
                      ]}
                      value={selectedTemplate}
                      onChange={(value: number) => handleApplyTemplateSelect(value)}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Toggle
                      label={t("Display Seat Numbers")}
                      checked={!!floorPlanEditState?.floorPlan?.display_seat_numbers}
                      onChange={e =>
                        setFloorPlanEditState(prev => ({
                          ...prev,
                          floorPlan: { ...prev.floorPlan, display_seat_numbers: +e.target.checked },
                        }))
                      }
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>

            <Card
              title={t("Kitchen Locations")}
              subtitle={t("Select the default kitchen location(s) for sending and printing items.")}
            >
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <Select
                      onChange={(value: any) => handleDropDownChange(value, "kitchen_location_id_1")}
                      defaultValue={floorPlanEditState.floorPlan.kitchen_location_id_1}
                    >
                      {floorPlanEditState.kitchenLocations
                        // Filter out other selected kitchen_location_ids
                        .filter((location: any) => {
                          if (location.id === -1) {
                            return location;
                          } else {
                            if (
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_2 &&
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_3
                            ) {
                              return location;
                            }
                          }
                        })
                        .map((location: any) => {
                          return (
                            <Option key={location.id} value={location.id} name={location.title}>
                              {location.title}
                            </Option>
                          );
                        })}
                    </Select>

                    <Select
                      onChange={(value: any) => handleDropDownChange(value, "kitchen_location_id_2")}
                      defaultValue={floorPlanEditState.floorPlan.kitchen_location_id_2}
                    >
                      {floorPlanEditState.kitchenLocations
                        // Filter out other selected kitchen_location_ids
                        .filter((location: any) => {
                          if (location.id === -1) {
                            return location;
                          } else {
                            if (
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_1 &&
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_3
                            ) {
                              return location;
                            }
                          }
                        })
                        .map((location: any) => {
                          return (
                            <Option key={location.id} value={location.id} name={location.title}>
                              {location.title}
                            </Option>
                          );
                        })}
                    </Select>
                    <Select
                      onChange={(value: any) => handleDropDownChange(value, "kitchen_location_id_3")}
                      defaultValue={floorPlanEditState.floorPlan.kitchen_location_id_3}
                    >
                      {floorPlanEditState.kitchenLocations
                        // Filter out other selected kitchen_location_ids
                        .filter((location: any) => {
                          if (location.id === -1) {
                            return location;
                          } else {
                            if (
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_1 &&
                              location.id !== floorPlanEditState.floorPlan.kitchen_location_id_2
                            ) {
                              return location;
                            }
                          }
                        })
                        .map((location: any) => {
                          return (
                            <Option key={location.id} value={location.id} name={location.title}>
                              {location.title}
                            </Option>
                          );
                        })}
                    </Select>
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>

            <Card style={{ marginBottom: "320px" }}>
              <Card.Section title={t("Floor Plan")}>
                <DndProvider backend={HTML5Backend}>
                  <div className="floor-plan-view">
                    <div
                      className="floor-plan-view-table-elements"
                      style={{
                        gridTemplateColumns: `repeat(auto-fill, minmax(${DEFAULT_TABLE_DIMENSION}px, 1fr))`,
                      }}
                    >
                      {floorPlanEditState.floorPlanTableElements.map((element, i) => {
                        return (
                          <div key={`option-${i}`}>
                            <FloorPlanTable optionTable={{ element }} />
                          </div>
                        );
                      })}
                    </div>
                    <FloorPlan
                      tables={floorPlanEditState.floorPlanTables}
                      setTables={setFloorPlanTables}
                      tableElements={floorPlanEditState.floorPlanTableElements}
                      floorPlanId={floorPlanEditState.floorPlanId}
                      gridWidth={floorPlanEditState.floorPlan.grid_width}
                      gridHeight={floorPlanEditState.floorPlan.grid_height}
                      tablesBoundingRectangles={floorPlanEditState.floorPlanTablesBoundingRectangles}
                      setTablesBoundingRectangles={setFloorPlanTablesBoundingRectangles}
                    />
                  </div>
                </DndProvider>
              </Card.Section>
            </Card>
          </div>
        )}

      <Popup
        open={floorPlanEditState.deleteFloorPlanWarning}
        type="warning"
        title={t("Delete Floor Plan?")}
        description={t("Are you sure you want to delete this floor plan?")}
        onOk={deleteFloorPlan}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        onCancel={() => setFloorPlanEditState(prev => ({ ...prev, deleteFloorPlanWarning: false }))}
      />
    </Page>
  );
}

/**
 *  Returns null if negative number.
 *  NULL kitchen_location_id defaults to -1 ("None")
 *  - Convert "None" kitchen_location_id to NULL before PUT call.
 */
export const negativeCheckHelper = (value: number) => {
  return value < 0 ? null : value;
};
